/**
 * CommentListReq 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'

/**
 * CommentListReq
 */
export interface CommentListReq {
  /** 搜索关键词 */
  q?: string
  /** 是否删除 */
  deleted?: boolean
  /** 页码 */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 排序字段 */
  order_by?: string
  /** 排序方向 */
  order?: string
  /** 创建时间范围 (start,end) */
  created_at__range?: any
  /** 更新时间范围 (start,end) */
  updated_at__range?: any
  /** 更新时间距今 (7d/1h/30m) */
  updated_at__since?: any
  /** 禁用状态 */
  disabled?: boolean
  /** ID列表 */
  id__in?: string[]
  /** 目标类型 */
  target_type?: string
  /** 目标ID */
  target_id?: string
  /** 评论类型 */
  type?: string
  /** 创建用户ID */
  create_user_id?: any
  /** 父评论ID */
  parent_comment_id?: any
  /** 回复的评论ID */
  reply_to_comment_id?: any
  /** 作者ID */
  author_id?: any
  /** 评论深度 */
  depth?: number
  /** 最小深度 */
  depth__gte?: number
  /** 最大深度 */
  depth__lte?: number
  /** 是否隐藏 */
  is_hidden?: boolean
  /** 是否折叠 */
  is_collapsed?: boolean
  /** 最小点赞数 */
  like_count__gte?: number
  /** 回复数范围 */
  reply_count__range?: string
  /** 内容包含 */
  content__like?: string
  /** 目标类型列表 */
  target_type__in?: string[]
  /** 创建用户ID列表 */
  create_user_id__in?: any
}

/**
 * CommentListReq Schema
 */
export const CommentListReqSchema: SchemaDefinition = {
  properties: {
    q: {
      description: '搜索关键词',
      format: 'string',
      type: 'string',
      label: '搜索关键词',
    },
    deleted: {
      description: '是否删除',
      format: '*bool',
      type: 'boolean',
      label: '是否删除',
    },
    page: {
      description: '页码',
      format: 'int',
      type: 'integer',
      label: '页码',
    },
    page_size: {
      description: '每页数量',
      format: 'int',
      type: 'integer',
      label: '每页数量',
    },
    order_by: {
      description: '排序字段',
      format: 'string',
      type: 'string',
      label: '排序字段',
    },
    order: {
      description: '排序方向',
      format: 'string',
      type: 'string',
      label: '排序方向',
    },
    created_at__range: {
      description: '创建时间范围 (start,end)',
      format: '[]string',
      type: 'datetime',
      label: '创建时间范围',
    },
    updated_at__range: {
      description: '更新时间范围 (start,end)',
      format: '[]string',
      type: 'datetime',
      label: '更新时间范围',
    },
    updated_at__since: {
      description: '更新时间距今 (7d/1h/30m)',
      format: '[]string',
      type: 'datetime',
      label: '更新时间距今',
    },
    disabled: {
      description: '禁用状态',
      format: '*bool',
      type: 'boolean',
      label: '禁用状态',
    },
    id__in: {
      description: 'ID列表',
      format: '[]string',
      items: {
        format: 'string',
        type: 'string',
      },
      type: 'array',
      label: 'ID列表',
    },
    target_type: {
      description: '目标类型',
      enum: ['post,comment,group'],
      format: 'cons.CommentTargetType',
      type: 'string',
      label: '目标类型',
    },
    target_id: {
      description: '目标ID',
      format: 'string',
      type: 'string',
      label: '目标ID',
    },
    type: {
      description: '评论类型',
      format: 'cons.CommentType',
      type: 'string',
      label: '评论类型',
    },
    create_user_id: {
      description: '创建用户ID',
      format: 'string',
      type: 'user',
      label: '创建用户ID',
    },
    parent_comment_id: {
      description: '父评论ID',
      format: 'string',
      type: 'comment',
      label: '父评论ID',
    },
    reply_to_comment_id: {
      description: '回复的评论ID',
      format: 'string',
      type: 'comment',
      label: '回复的评论ID',
    },
    author_id: {
      description: '作者ID',
      format: 'string',
      type: 'user',
      label: '作者ID',
    },
    depth: {
      description: '评论深度',
      format: '*int',
      type: 'integer',
      label: '评论深度',
    },
    depth__gte: {
      description: '最小深度',
      format: '*int',
      type: 'integer',
      label: '最小深度',
    },
    depth__lte: {
      description: '最大深度',
      format: '*int',
      type: 'integer',
      label: '最大深度',
    },
    is_hidden: {
      description: '是否隐藏',
      format: '*bool',
      type: 'boolean',
      label: '是否隐藏',
    },
    is_collapsed: {
      description: '是否折叠',
      format: '*bool',
      type: 'boolean',
      label: '是否折叠',
    },
    like_count__gte: {
      description: '最小点赞数',
      format: '*int',
      type: 'integer',
      label: '最小点赞数',
    },
    reply_count__range: {
      description: '回复数范围',
      format: '*string',
      type: 'string',
      label: '回复数范围',
    },
    content__like: {
      description: '内容包含',
      format: 'string',
      type: 'string',
      label: '内容包含',
    },
    target_type__in: {
      description: '目标类型列表',
      format: '[]string',
      items: {
        format: 'string',
        type: 'string',
      },
      type: 'array',
      label: '目标类型列表',
    },
    create_user_id__in: {
      description: '创建用户ID列表',
      format: '[]string',
      type: 'user',
      label: '创建用户ID列表',
    },
  },
  type: 'object',
}
